<template>
  <div>
    <div class="kb-list-head">
      <span
        v-if="childrenArray.length === 0"
        class="kb-row-meta"
      >No children added yet</span>
      <KbStats
        v-else
        size="sm"
        :items="[
          { label: childrenArray.length === 1 ? 'child' : 'children', value: childrenArray.length },
          {
            label: 'with a ticket',
            value: childrenArray.length - childrenWithoutTickets.length,
            tone: childrenWithoutTickets.length === 0 ? 'ok' : 'warn',
            hidden: !(showTicketButtons || inAdminContext),
          },
        ]"
      />
      <div
        v-if="!disabled"
        class="flex flex-wrap items-center gap-1"
      >
        <Button
          label="Add child"
          icon="pi pi-plus"
          text
          size="small"
          @click="addNewChild"
        />
        <!-- Buy tickets for every child that still needs one -->
        <SharedPurchaseButton
          v-if="showTicketButtons && childrenWithoutTickets.length > 0"
          ticket-type="children"
          button-label="Buy missing tickets"
          button-size="small"
          icon="pi pi-ticket"
          :full-width="false"
          :children-ids="childrenWithoutTickets.map(child => child.id)"
          @order-complete="handleAllTicketsPurchased"
        />
        <!-- Admin mark all as paid button -->
        <Button
          v-if="inAdminContext && childrenWithoutTickets.length > 0"
          label="Mark all paid"
          icon="pi pi-check"
          text
          severity="secondary"
          size="small"
          :loading="isUpdating"
          @click="markAllChildrenAsPaid"
        />
      </div>
    </div>

    <ul
      v-if="childrenArray.length > 0"
      class="kb-rows m-0 p-0 list-none"
    >
      <li
        v-for="child in childrenArray"
        :key="child.id"
        class="kb-row"
        :class="{ 'kb-row-link': !disabled }"
      >
        <!-- The whole row opens the edit dialog (which also holds Remove) -->
        <button
          type="button"
          class="kb-row-open"
          :disabled="disabled"
          @click="editChild(child)"
        >
          <span class="kb-row-title block">
            {{ child.firstName }} {{ child.lastName }}<template v-if="showTicketButtons || inAdminContext">
              <span
                v-if="hasChildTicket(child)"
                class="kb-tag kb-tag-ok"
              >Ticket</span>
              <span
                v-else
                class="kb-tag kb-tag-warn"
              >No ticket</span>
            </template>
          </span>
          <span class="kb-row-meta block mt-0.5">
            Born {{ formatDate(child.birthday) }}
            <template v-if="(showTicketButtons || inAdminContext) && hasChildTicket(child)">
              · ticket bought {{ formatDate(getChildTicketPurchaseDate(child)) }}
              <template v-if="getChildTicketOrderId(child)">
                · order {{ getChildTicketOrderId(child) }}<template v-if="isAdminCreatedOrder(getChildTicketOrderId(child))">
                  (added by an admin)
                </template>
              </template>
            </template>
          </span>
        </button>

        <div class="kb-row-aside">
          <!-- Admin: mark this child's ticket as paid or unpaid -->
          <Button
            v-if="inAdminContext && !hasChildTicket(child) && !disabled"
            label="Mark paid"
            text
            severity="secondary"
            size="small"
            :loading="isUpdating"
            @click="markChildAsPaid(child)"
          />
          <Button
            v-if="inAdminContext && hasChildTicket(child) && isAdminCreatedOrder(getChildTicketOrderId(child)) && !disabled"
            label="Mark unpaid"
            text
            severity="danger"
            size="small"
            :loading="isUpdating"
            @click="markChildAsUnpaid(child)"
          />

          <!-- Member: buy this child's ticket -->
          <SharedPurchaseButton
            v-if="showTicketButtons && !hasChildTicket(child) && !disabled"
            ticket-type="children"
            button-label="Get ticket"
            button-class="get-ticket-button p-button-outlined"
            button-size="small"
            :button-icon-only="isMobile"
            icon="pi pi-ticket"
            :full-width="false"
            :child-id="child.id"
            @order-complete="handleTicketPurchased(child)"
          />
          <i
            v-if="!disabled"
            class="pi pi-angle-right kb-row-chevron"
            aria-hidden="true"
          />
        </div>
      </li>
    </ul>

    <!-- Add / edit a child -->
    <Dialog
      v-model:visible="showChildDialog"
      :header="editingChild ? 'Edit child' : 'Add child'"
      modal
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      class="child-form-dialog"
    >
      <div class="kb-form">
        <div class="kb-form-pair">
          <div class="kb-field">
            <label for="firstName">First name</label>
            <InputText
              id="firstName"
              v-model="childForm.firstName"
              size="small"
              fluid
              autofocus
              :invalid="submitted && !childForm.firstName"
              aria-describedby="firstName-error"
              :disabled="disabled"
            />
            <small
              v-if="submitted && !childForm.firstName"
              id="firstName-error"
              class="kb-field-error"
            >
              First name is required
            </small>
          </div>

          <div class="kb-field">
            <label for="lastName">Last name</label>
            <InputText
              id="lastName"
              v-model="childForm.lastName"
              size="small"
              fluid
              :invalid="submitted && !childForm.lastName"
              aria-describedby="lastName-error"
              :disabled="disabled"
            />
            <small
              v-if="submitted && !childForm.lastName"
              id="lastName-error"
              class="kb-field-error"
            >
              Last name is required
            </small>
          </div>
        </div>

        <div class="kb-field">
          <label for="birthday">Date of birth</label>
          <InputText
            id="birthday"
            v-model="childForm.birthday"
            type="date"
            size="small"
            fluid
            :invalid="submitted && (!childForm.birthday || !isValidChildAge)"
            aria-describedby="birthday-hint birthday-error"
            :max="formatDateForInput(new Date())"
            :min="minChildBirthday"
            :disabled="disabled"
          />
          <small
            v-if="submitted && !childForm.birthday"
            id="birthday-error"
            class="kb-field-error"
          >
            Date of birth is required
          </small>
          <small
            v-else-if="submitted && childForm.birthday && !isValidChildAge"
            id="birthday-error"
            class="kb-field-error"
          >
            Children must be under 18; older kids need their own membership
          </small>
          <small
            v-else
            id="birthday-hint"
            class="kb-field-hint"
          >
            Under 18
          </small>
        </div>
      </div>

      <template #footer>
        <Button
          v-if="editingChild && !disabled"
          label="Remove"
          icon="pi pi-trash"
          text
          severity="danger"
          class="mr-auto"
          @click="removeEditingChild"
        />
        <Button
          label="Cancel"
          text
          severity="secondary"
          @click="closeDialog"
        />
        <Button
          :label="editingChild ? 'Save' : 'Add child'"
          :disabled="disabled"
          @click="saveChildForm"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useCurrentUser } from 'vuefire'
import type { ChildPurchaseItem } from '~/shared/types/member.js'
import { useMembersStore } from '~/stores/members'

// Import the date utility and runtime config
import { formatDate, formatDateForInput, get18YearsAgoPlusOneDay, isAtLeast18 } from '~/utils/dateUtils'

interface Child {
  id: string
  firstName: string
  lastName: string
  birthday: Date | string
  created_at?: Date
  // Legacy fields (keeping for backwards compatibility)
  hasTicket?: boolean
  ticketPurchasedAt?: Date
  ticketOrderId?: string
}

const props = defineProps({
  // Keep the children prop for backwards compatibility
  children: {
    type: Object as () => Record<string, Omit<Child, 'id'>>,
    default: () => ({}),
  },
  // Add a new prop for the complete member object
  member: {
    type: Object as () => Member,
    default: () => null,
  },
  showTicketButtons: {
    type: Boolean,
    default: false,
  },
  inAdminContext: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

// Get the current parade year from config
const { paradeYear } = useParadeYear()

// Get the membersStore to access latest member data
const membersStore = useMembersStore()
// Get current user for tracking who comps tickets
const currentUser = useCurrentUser()
// Get the current user's ID for reactive updates
const memberId = computed(() => props.member?.id || null)

// Create a reactive reference to the current member object from the store
const currentMemberFromStore = computed(() => {
  if (!memberId.value) return null
  const memberPath = `members/${memberId.value}`
  return membersStore.members[memberPath] || props.member
})

// Convert map to array for display
const childrenArray = computed(() => {
  // Using currentMemberFromStore ensures we always have the latest purchase data
  // This dependency ensures the computed property updates when purchases change
  const latestMember = currentMemberFromStore.value || props.member

  return Object.entries(props.children || {}).map(([id, data]) => ({
    id,
    ...data,
    // Calculate ticket status dynamically
    _hasTicket: hasChildTicket({ id, ...data }, latestMember),
  }))
})

// A child has a ticket when a completed children purchase for the parade
// year lists them, as check-in counts it (useCheckinStatus)
function hasChildTicket(child: Child, memberData = currentMemberFromStore.value || props.member): boolean {
  if (memberData?.purchases?.[paradeYear.value]?.children) {
    // Look through all children ticket purchases for this year
    const childrenPurchases = memberData.purchases[paradeYear.value]?.children
    if (Array.isArray(childrenPurchases)) {
      // Check if any purchase includes this child's ID in childrenIds
      return childrenPurchases.some(purchase =>
        purchase.status === 'completed'
        && Array.isArray(purchase.childrenIds)
        && purchase.childrenIds.includes(child.id),
      )
    }
  }

  return false
}

// Helper function to get child ticket purchase date
function getChildTicketPurchaseDate(child: Child): Date | null {
  const memberData = currentMemberFromStore.value || props.member

  // First check legacy field
  if (child.ticketPurchasedAt) {
    return child.ticketPurchasedAt
  }

  // Then check new structure
  if (memberData?.purchases?.[paradeYear.value]?.children) {
    const childrenPurchases = memberData.purchases[paradeYear.value]?.children
    if (Array.isArray(childrenPurchases)) {
      // Find the purchase that includes this child's ID
      const purchase = childrenPurchases.find(p =>
        p.status === 'completed'
        && Array.isArray(p.childrenIds)
        && p.childrenIds.includes(child.id),
      )

      if (purchase?.purchasedAt) {
        return purchase.purchasedAt
      }
    }
  }

  return null
}

// Helper function to get child ticket order ID
function getChildTicketOrderId(child: Child): string | null {
  const memberData = currentMemberFromStore.value || props.member

  // First check legacy field
  if (child.ticketOrderId) {
    return child.ticketOrderId
  }

  // Then check new structure
  if (memberData?.purchases?.[paradeYear.value]?.children) {
    const childrenPurchases = memberData.purchases[paradeYear.value]?.children
    if (Array.isArray(childrenPurchases)) {
      // Find the purchase that includes this child's ID
      const purchase = childrenPurchases.find(p =>
        p.status === 'completed'
        && Array.isArray(p.childrenIds)
        && p.childrenIds.includes(child.id),
      )

      if (purchase?.orderId) {
        return purchase.orderId
      }
    }
  }

  return null
}

// Helper function to check if an order was admin-created
function isAdminCreatedOrder(orderId: string | null): boolean {
  // Logic to determine if the order was admin-created
  // For example, check if the orderId starts with a specific prefix
  return orderId?.startsWith('admin-') || false
}

// This year's children-ticket update: the children tickets and the order ids,
// nothing else. Writing back the whole year from this list's copy could undo
// a payment the Stripe webhook recorded since the page loaded, so the lists
// come from the member's live record.
async function childrenTicketsUpdate(
  change: (children: ChildPurchaseItem[], orderIds: string[]) => { children: ChildPurchaseItem[], orderIds: string[] },
): Promise<Record<string, { children: ChildPurchaseItem[], orderIds: string[] }>> {
  const fresh = await membersStore.fetchMember(props.member.id)
  const year = (fresh ?? currentMemberFromStore.value ?? props.member).purchases?.[paradeYear.value]
  return { [paradeYear.value]: change([...(year?.children ?? [])], [...(year?.orderIds ?? [])]) }
}

// Count children without tickets - reactive computed property that updates when member changes
const childrenWithoutTickets = computed(() => {
  return childrenArray.value.filter(child => !hasChildTicket(child))
})

// Mobile detection
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0)
const isMobile = computed(() => windowWidth.value < 640)

// Handle window resize for mobile detection
const handleResize = () => {
  windowWidth.value = window.innerWidth
}

// Setup resize listener
onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
    handleResize() // Initialize on mount
  }
})

// Clean up event listener
onUnmounted(() => {
  if (typeof window !== 'undefined')
    window.removeEventListener('resize', handleResize)
})

const emit = defineEmits(['add', 'update', 'remove', 'purchase-ticket', 'purchase-all-tickets'])

const confirm = useConfirm()
const toast = useToast()
const showChildDialog = ref(false)
const editingChild = ref<Child | null>(null)
const submitted = ref(false)
const isUpdating = ref(false)

// Child form with validation
const childForm = reactive({
  firstName: '',
  lastName: '',
  birthday: '',
})

// The earliest birthday of a child: one who turns 18 tomorrow
const minChildBirthday = formatDateForInput(get18YearsAgoPlusOneDay())

// Children are under 18, counted by local calendar day
const isValidChildAge = computed(() => !childForm.birthday || !isAtLeast18(childForm.birthday))

// Reset form to default values or editing values
const resetForm = () => {
  if (editingChild.value) {
    childForm.firstName = editingChild.value.firstName
    childForm.lastName = editingChild.value.lastName
    childForm.birthday = formatDateForInput(editingChild.value.birthday)
  }
  else {
    childForm.firstName = ''
    childForm.lastName = ''
    childForm.birthday = ''
  }
  submitted.value = false
}

const addNewChild = () => {
  if (props.disabled) return
  editingChild.value = null
  resetForm()
  showChildDialog.value = true
}

const editChild = (child: Child) => {
  if (props.disabled) return
  editingChild.value = { ...child }
  resetForm() // Will initialize form with editingChild values
  showChildDialog.value = true
}

const closeDialog = () => {
  showChildDialog.value = false
}

// Validate the form manually
const validateForm = () => {
  submitted.value = true

  if (!childForm.firstName || !childForm.lastName || !childForm.birthday || !isValidChildAge.value) {
    return false
  }

  return true
}

// Save the child form
const saveChildForm = () => {
  if (props.disabled) return
  if (!validateForm()) {
    return
  }

  try {
    if (editingChild.value) {
      // Update existing child
      emit('update', {
        ...editingChild.value,
        firstName: childForm.firstName,
        lastName: childForm.lastName,
        birthday: childForm.birthday,
      })
    }
    else {
      // Add new child with unique ID
      emit('add', {
        id: crypto.randomUUID(), // Generate a unique ID
        firstName: childForm.firstName,
        lastName: childForm.lastName,
        birthday: childForm.birthday,
        created_at: new Date(),
      })
    }

    // The parent saves the child and says how that went
    closeDialog()
  }
  catch (error) {
    console.error('Error saving child:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save child information',
      life: 3000,
    })
  }
}

const confirmRemoveChild = (child: Child) => {
  if (props.disabled) return
  confirm.require({
    message: `Are you sure you want to remove ${child.firstName} ${child.lastName}?`,
    header: 'Confirm Removal',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: () => {
      emit('remove', child)
    },
  })
}

// Remove lives in the edit dialog now: close it, then confirm as before
const removeEditingChild = () => {
  const child = editingChild.value
  if (!child) return
  showChildDialog.value = false
  confirmRemoveChild(child)
}

// Handlers for ticket purchases
const handleTicketPurchased = (child: Child) => {
  // After purchase, we don't need to manually update the UI
  // The store subscription system will update the component automatically
  // since we're using the reactive currentMemberFromStore computed property

  // Still emit an event for parent components
  emit('purchase-ticket', {
    childId: child.id,
    completed: true,
  })

  toast.add({
    severity: 'success',
    summary: 'Ticket Purchased',
    detail: `Ticket for ${child.firstName} ${child.lastName} has been purchased`,
    life: 3000,
  })
}

const handleAllTicketsPurchased = (event: unknown) => {
  // After purchase, we don't need to manually update the UI
  // The store subscription system will update the component automatically
  // using the reactive computed properties

  // Emit an event with the entire purchase data for parent handling
  emit('purchase-all-tickets', event)

  toast.add({
    severity: 'success',
    summary: 'Tickets Purchased',
    detail: `Tickets for ${childrenWithoutTickets.value.length} children have been purchased`,
    life: 3000,
  })
}

// Admin handlers for marking tickets as paid/unpaid
const markChildAsPaid = async (child: Child) => {
  if (props.disabled) return
  if (!props.member?.id) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Member ID not found',
      life: 3000,
    })
    return
  }

  isUpdating.value = true
  try {
    const now = new Date()
    const adminOrderId = `admin-${Date.now()}-${crypto.randomUUID().substring(0, 8)}`

    // Create purchase object in the new format - similar to what's in PaymentStatusSection
    const purchaseItem = {
      orderId: adminOrderId,
      purchasedAt: now,
      ticketType: 'children',
      accessCode: 'admin-override',
      status: 'completed',
      eventbriteOrderId: adminOrderId,
      childrenIds: [child.id],
      quantity: 1,
      issuedBy: currentUser.value?.uid || 'unknown',
    }

    // Add the ticket next to the year's other children tickets and order ids
    const purchasesUpdate = await childrenTicketsUpdate((children, orderIds) => ({
      children: [...children, purchaseItem],
      orderIds: orderIds.includes(adminOrderId) ? orderIds : [...orderIds, adminOrderId],
    }))

    // Update the member in Firestore - use a proper type cast
    const saved = await membersStore.updateMember(props.member, {
      purchases: purchasesUpdate,
    } as Partial<Member>)
    if (!saved) {
      throw new Error('The member update was not saved')
    }

    toast.add({
      severity: 'success',
      summary: 'Marked as Paid',
      detail: `${child.firstName} ${child.lastName} has been marked as paid`,
      life: 3000,
    })
  }
  catch (error) {
    console.error('Error marking child as paid:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to mark child as paid',
      life: 3000,
    })
  }
  finally {
    isUpdating.value = false
  }
}

const markChildAsUnpaid = async (child: Child) => {
  if (props.disabled) return
  if (!props.member?.id) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Member ID not found',
      life: 3000,
    })
    return
  }

  isUpdating.value = true
  try {
    // Find purchase for this child
    const childTicketOrderId = getChildTicketOrderId(child)

    // Only allow removing admin-created purchases
    if (!childTicketOrderId || !isAdminCreatedOrder(childTicketOrderId)) {
      throw new Error('Cannot remove non-admin purchases')
    }

    // Take back only this child's admin-created ticket
    const isThisTicket = (purchase: ChildPurchaseItem) => purchase.orderId === childTicketOrderId
      && Boolean(purchase.childrenIds?.includes(child.id))
      && isAdminCreatedOrder(purchase.orderId)
    const purchasesUpdate = await childrenTicketsUpdate((children, orderIds) => {
      const remaining = children.filter(purchase => !isThisTicket(purchase))
      return {
        children: remaining,
        orderIds: remaining.some(p => p.orderId === childTicketOrderId) ? orderIds : orderIds.filter(id => id !== childTicketOrderId),
      }
    })

    // Update the member in Firestore - use a proper type cast
    const saved = await membersStore.updateMember(props.member, {
      purchases: purchasesUpdate,
    } as Partial<Member>)
    if (!saved) {
      throw new Error('The member update was not saved')
    }

    toast.add({
      severity: 'success',
      summary: 'Marked as Unpaid',
      detail: `${child.firstName} ${child.lastName} has been marked as unpaid`,
      life: 3000,
    })
  }
  catch (error) {
    console.error('Error marking child as unpaid:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error instanceof Error ? error.message : 'Failed to mark child as unpaid',
      life: 3000,
    })
  }
  finally {
    isUpdating.value = false
  }
}

const markAllChildrenAsPaid = async () => {
  if (props.disabled) return
  if (!props.member?.id || childrenWithoutTickets.value.length === 0) {
    return
  }

  isUpdating.value = true
  try {
    const now = new Date()
    const adminOrderId = `admin-${Date.now()}-${crypto.randomUUID().substring(0, 8)}`

    // Get IDs of all children without tickets
    const childIds = childrenWithoutTickets.value.map(child => child.id)

    // Create a single purchase for all children
    const purchaseItem = {
      orderId: adminOrderId,
      purchasedAt: now,
      ticketType: 'children',
      accessCode: 'admin-override',
      status: 'completed',
      eventbriteOrderId: adminOrderId,
      childrenIds: childIds,
      quantity: childIds.length,
      issuedBy: currentUser.value?.uid || 'unknown',
    }

    // Add the ticket next to the year's other children tickets and order ids
    const purchasesUpdate = await childrenTicketsUpdate((children, orderIds) => ({
      children: [...children, purchaseItem],
      orderIds: orderIds.includes(adminOrderId) ? orderIds : [...orderIds, adminOrderId],
    }))

    // Update the member in Firestore - use a proper type cast
    const saved = await membersStore.updateMember(props.member, {
      purchases: purchasesUpdate,
    } as Partial<Member>)
    if (!saved) {
      throw new Error('The member update was not saved')
    }

    toast.add({
      severity: 'success',
      summary: 'All Marked as Paid',
      detail: `All children (${childIds.length}) have been marked as paid`,
      life: 3000,
    })
  }
  catch (error) {
    console.error('Error marking all children as paid:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to mark all children as paid',
      life: 3000,
    })
  }
  finally {
    isUpdating.value = false
  }
}

// Watch for changes to the member object in the store
// This ensures we update the UI when purchases are made in other components
watch(() => currentMemberFromStore.value, () => {
  // This reactively updates the UI when the store changes
  // No manual handling needed, the computed properties will recalculate
}, { deep: true })
</script>

<style scoped>
/* Add specific styling for child form dialog */
:deep(.child-form-dialog .p-dialog-content) {
  overflow: visible !important;
}
</style>
